import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/Button';
import { canUseFavorites } from '@/constants/role-policy';
import { useLogout } from '@/hooks/useAuth';
import { useFavoriteIds } from '@/hooks/useFavorites';
import { useAuthStore } from '@/stores/auth-store';

export function AppLayoutActions() {
  const user = useAuthStore((state) => state.user);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const logoutMutation = useLogout();
  const canAccessFavorites = canUseFavorites(user?.roles);
  const { data: favoriteIdsData } = useFavoriteIds({ enabled: canAccessFavorites });
  const favoriteCount = canAccessFavorites ? favoriteIdsData?.count ?? 0 : 0;

  return (
    <div className="flex flex-wrap items-center gap-3" data-ai-id="app-layout-actions">
      {canAccessFavorites ? (
        <Link
          to="/my-page/favorites"
          className="inline-flex items-center gap-2 border border-[#ccc] bg-[#f5f5f5] px-3 py-2 text-sm text-[#333] transition-none hover:bg-[#e6e6e6] hover:underline focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-ai-id="app-layout-my-page-link"
        >
          <span data-ai-id="app-layout-my-page-link-label">Saved</span>
          {favoriteCount > 0 ? (
            <span
              className="inline-flex min-w-5 origin-center items-center justify-center border border-blue-800 bg-blue-600 px-1.5 py-0.5 text-xs font-bold text-white"
              data-ai-id="app-layout-favorites-count-badge"
              aria-label={`Saved items ${favoriteCount}`}
            >
              {favoriteCount}
            </span>
          ) : null}
        </Link>
      ) : (
        <Link
          to="/login"
          search={{ redirect: '/my-page/favorites' }}
          className="inline-flex items-center gap-2 border border-[#ccc] bg-[#f5f5f5] px-3 py-2 text-sm text-[#333] transition-none hover:bg-[#e6e6e6] hover:underline focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-ai-id="app-layout-my-page-login-link"
        >
          <span data-ai-id="app-layout-my-page-login-link-label">Saved</span>
        </Link>
      )}

      {user ? (
        <div className="text-sm text-slate-500" data-ai-id="app-layout-user-display">
          {user.displayName}
        </div>
      ) : null}

      {isLoggedIn ? (
        <Button
          variant="secondary"
          size="sm"
          loading={logoutMutation.isPending}
          onClick={() => logoutMutation.mutate()}
          data-ai-id="app-layout-logout-button"
          aiIdPrefix="app-layout-logout-button"
        >
          Sign out
        </Button>
      ) : (
        <Link
          to="/login"
          className="inline-flex h-8 items-center justify-center border border-[#ccc] bg-[#f5f5f5] px-3 text-xs font-bold text-[#333] transition-none hover:bg-[#e6e6e6] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          data-ai-id="app-layout-login-link"
        >
          Sign in
        </Link>
      )}
    </div>
  );
}
